import {
    Box,
    Text,
    Image,
    HStack,
    Avatar,
    useToast,
    IconButton,
    Icon,
    Heading,
    ListItem,
    UnorderedList, OrderedList, Td, Th, Table, Thead, Tbody, Tr, Link, Tooltip, Button,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
    VStack,
} from "@chakra-ui/react";
import {whichMessageHeader} from "../../utils/pdf-page-helpers";
import React from "react";
import {ThreeDotLoading} from "../Reuseable/ThreeDotLoading";
import {useAuth} from "../../redux/AuthProvider";
import FollowUpButton from "./FollowUpButton";
import lightTheme from "../../utils/styles";
import {FiCopy, FiCornerDownRight} from "react-icons/fi";
import { MdOutlineArrowOutward } from "react-icons/md";
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks';
import {markdownComponents, copyFormattedToClipboard} from "../../utils/constants";
import {useTranslation} from "react-i18next";
import {ThinkingDisplay} from "./ThinkingDisplay";
import LargeContextThinkingDisplay from "./LargeContextThinkingDisplay";


export const MessageBubbleMultidoc = React.memo(({ message, isLastMessage, userBgColor, claudeBgColor, claudeColor,
                                                     lastMessageRef, sessionToken, socketID, onCitationClick, currentThinkingMessage, isThinking, socketGeneration,
                                                     isLargeContextProcessing = false, largeContextTaskId = null, largeContextProgressHistory = [] }) => {
    // Debug: Log when this component receives progressHistory
    // if (isLargeContextProcessing && message.user === "Gemini" && message.response === "") {
    //     console.log("[MessageBubbleMultidoc] Rendering with progressHistory:", largeContextProgressHistory?.length || 0, "items", largeContextProgressHistory);
    // }

    const auth = useAuth();
    const toast = useToast();
    const [firstName, lastName] = [localStorage.getItem("firstName"), localStorage.getItem("lastName")];
    const {t} = useTranslation();
    let citationMap = new Map();


    const renderCitation = (citation, citationMap, boundingRect = null, citationText = null) => {
        const match = citation.match(/\[(.*?\.pdf),\s*(pages?\s*[\d,\-–\s]+(?:,\s*pages?\s*\d+)*)\]/i);
        if (!match) return citation;

        const [_, filename, pagesRaw] = match;
        const citationKey = `${filename.trim()}_${pagesRaw.trim()}`;
        const citationNumber = citationMap.get(citationKey);

        // Parse individual page numbers from formats like "page 12, page 34" or "pages 1-3" or "page 5"
        const pageNumbers = pagesRaw.match(/\d+/g)?.map(Number) || [];
        const firstPage = pageNumbers[0] || 1;

        const handleCitationClick = () => {
            onCitationClick(filename.trim(), String(firstPage), boundingRect, citationText);
        };

        // Multi-page citation: show popover with per-page buttons on hover
        if (pageNumbers.length > 1) {
            return (
                <Popover trigger="hover" placement="top" openDelay={100} closeDelay={200}>
                    <PopoverTrigger>
                        <Button
                            size="xs"
                            variant="outline"
                            colorScheme="blue"
                            onClick={handleCitationClick}
                            mx="1"
                            display="inline-block"
                        >
                            [{citationNumber}]
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent width="auto" maxW="280px">
                        <PopoverBody p={2}>
                            <Text fontSize="xs" fontWeight="bold" mb={1} noOfLines={1}>
                                {filename.trim()}
                            </Text>
                            <HStack spacing={1} flexWrap="wrap">
                                {pageNumbers.map((pg) => (
                                    <Button
                                        key={pg}
                                        size="xs"
                                        variant="solid"
                                        colorScheme="blue"
                                        onClick={() => onCitationClick(filename.trim(), String(pg), null, citationText)}
                                    >
                                        p. {pg}
                                    </Button>
                                ))}
                            </HStack>
                        </PopoverBody>
                    </PopoverContent>
                </Popover>
            );
        }

        // Single-page citation: simple tooltip
        return (
            <Tooltip
                hasArrow
                label={`${filename.trim()} (page ${firstPage})`}
                bg="black"
                color="white"
                placement="top"
            >
                <Button
                    size="xs"
                    variant="outline"
                    colorScheme="blue"
                    onClick={handleCitationClick}
                    mx="1"
                    display="inline-block"
                >
                    [{citationNumber}]
                </Button>
            </Tooltip>
        );
    };

    const escapeMarkdown = (text) => {
        return text
            .replace(/_/g, '\\_');    // Escape underscores
    };

    // Helper function to insert web citations into text
    const insertWebCitations = (text, supportsList = [], sourcesList = []) => {
        if (!supportsList || supportsList.length === 0) return text;
        
        // Sort supports by end_index in descending order to insert from end to beginning
        // This way, later insertions don't affect earlier indices
        const sortedSupports = [...supportsList].sort((a, b) => b.end_index - a.end_index);
        
        let modifiedText = text;
        
        // Process in reverse order (end to beginning) - no offset needed!
        sortedSupports.forEach((support) => {
            const { start_index, end_index, source_ids, text_segment } = support;
            
            // Verify the text segment matches (with some tolerance for whitespace)
            const expectedText = text_segment.trim();
            const actualText = modifiedText.substring(start_index, end_index).trim();
            
            // Check if the text matches or if we can find it nearby
            let insertPosition = end_index;
            
            if (expectedText !== actualText) {
                // Try to find the text segment in a nearby range (±100 chars for safety)
                const searchStart = Math.max(0, start_index - 100);
                const searchEnd = Math.min(modifiedText.length, end_index + 100);
                const searchText = modifiedText.substring(searchStart, searchEnd);
                const foundIndex = searchText.indexOf(expectedText);
                
                if (foundIndex !== -1) {
                    // Found it! Adjust insertion position
                    insertPosition = searchStart + foundIndex + expectedText.length;
                } else {
                    // Can't find exact match, try matching the last few words of the segment
                    const lastWords = expectedText.split(' ').slice(-3).join(' ');
                    const lastWordsIndex = modifiedText.indexOf(lastWords, searchStart);
                    if (lastWordsIndex !== -1) {
                        insertPosition = lastWordsIndex + lastWords.length;
                    } else {
                        // Fallback: try to find just the start of the segment and calculate end
                        const firstWords = expectedText.split(' ').slice(0, 5).join(' ');
                        const firstWordsIndex = modifiedText.indexOf(firstWords, searchStart);
                        if (firstWordsIndex !== -1) {
                            insertPosition = firstWordsIndex + expectedText.length;
                        } else {
                            // Last resort: use the original end_index
                            console.warn('Could not verify citation position for:', expectedText.substring(0, 50) + '...', 'using original index:', end_index);
                        }
                    }
                }
            }
            
            // Create citation markers with source IDs
            const citationMarkers = source_ids.map(id => `[WEB:${id}]`).join('');
            
            // Insert the citations
            const beforeText = modifiedText.substring(0, insertPosition);
            const afterText = modifiedText.substring(insertPosition);
            modifiedText = beforeText + citationMarkers + afterText;
        });
        
        return modifiedText;
    };

    const whichMessage = (message) => {
        if (message.user === 'User') {
            if (message.type.includes("selection")) {
                return <Text>{message.selection_text}</Text>
            } else if (message.type.includes("follow_up")) {
                if (message.selection_text) {
                    const formattedMessage = message.selection_text;
                    return <Markdown
                        remarkPlugins={[remarkGfm]}
                        components={markdownComponents}
                        children={formattedMessage} />
                } else {
                    return <Markdown
                        remarkPlugins={[remarkGfm]}
                        components={markdownComponents}
                        children={message.selection_text} />
                }
            }
            else {
                if (message.question) {
                    const formattedMessage = message.question;
                    return <Markdown
                        remarkPlugins={[remarkGfm]}
                        components={markdownComponents}
                        children={formattedMessage} />
                } else {
                    return <Markdown
                        remarkPlugins={[remarkGfm]}
                        components={markdownComponents}
                        children={message.question} />
                }
            }
        } else if (message.user === 'Gemini') {
            if (message.response) {
                // Insert web citations into the response text first
                let responseWithWebCitations = insertWebCitations(
                    message.response, 
                    message.supportsList || [], 
                    message.sourcesList || []
                );
                
                let formattedMessage = responseWithWebCitations.replace(/\n{2,}/g, '\n&nbsp;\n')
                    // Replace single newline with a line break
                    .replace(/\n/g, '  \n');

                // Get the count of web sources to offset PDF citations
                const webCitationCount = (message.sourcesList && message.sourcesList.length) || 0;
                
                // const citationMap = new Map();
                // PDF citations start after web citations
                let currentNumber = webCitationCount + 1;

                const citationRegex = /\[(.*?\.pdf),\s*(pages?\s*[\d,\-–\s]+(?:,\s*pages?\s*\d+)*)\]/gi;
                let match;
                while ((match = citationRegex.exec(formattedMessage)) !== null) {
                    const [_, filename, pages] = match;
                    const citationKey = `${filename.trim()}_${pages.trim()}`;
                    if (!citationMap.has(citationKey)) {
                        citationMap.set(citationKey, currentNumber++);
                    }
                }

                const renderWebCitation = (webCitationText, sourcesList) => {
                    // Extract source IDs from [WEB:1], [WEB:2], etc.
                    const sourceIds = webCitationText.match(/\[WEB:(\d+)\]/g) || [];
                    const uniqueIds = [...new Set(sourceIds.map(s => parseInt(s.match(/\d+/)[0])))];
                    
                    if (uniqueIds.length === 0 || !sourcesList) return null;
                    
                    const sources = uniqueIds.map(id => sourcesList.find(s => s.id === id)).filter(Boolean);
                    
                    return sources.map((source, idx) => {
                        // Use the title as the domain (it already contains the clean domain name)
                        const domain = source.title;
                        
                        return (
                            <Popover key={`web-${source.id}-${idx}`} trigger="hover" placement="top" openDelay={150} closeDelay={100}>
                                <PopoverTrigger>
                                    <Button
                                        mx="1"
                                        py={1}
                                        px={1}
                                        display="inline-block"
                                        onClick={() => window.open(source.url, '_blank')}
                                        size="xs"
                                        variant="outline"
                                        colorScheme="red"
                                    >
                                        <Icon as={MdOutlineArrowOutward} boxSize="3" mr={1}/>
                                        {source.id}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent 
                                    maxW="320px" 
                                    bg="white" 
                                    borderRadius="xl" 
                                    boxShadow="0 4px 20px rgba(0, 0, 0, 0.15)"
                                    border="1px solid"
                                    borderColor="gray.200"
                                    _focus={{ outline: "none" }}
                                >
                                    <PopoverBody p={4}>
                                        <VStack align="stretch" spacing={3}>
                                            {/* Header with domain and external link icon */}
                                            <HStack 
                                                justify="space-between" 
                                                pb={2} 
                                                borderBottom="1px solid" 
                                                borderColor="gray.100"
                                            >
                                                <HStack spacing={2}>
                                                    <Icon as={MdOutlineArrowOutward} boxSize="4" color="blue.500" />
                                                    <Text fontWeight="700" fontSize="sm" color="gray.800">
                                                        {domain}
                                                    </Text>
                                                </HStack>
                                            </HStack>
                                            
                                            {/* Title section */}
                                            <Box>
                                                <Text fontSize="xs" fontWeight="600" color="gray.600" mb={1}>
                                                    Source:
                                                </Text>
                                                <Text fontSize="xs" color="gray.700" lineHeight="1.5">
                                                    {source.title}
                                                </Text>
                                            </Box>
                                            
                                            {/* Relevance section */}
                                            <Box>
                                                <Text fontSize="xs" fontWeight="600" color="gray.600" mb={1}>
                                                    Relevance:
                                                </Text>
                                                <Text fontSize="xs" color="gray.700" lineHeight="1.5">
                                                    This source provides supporting evidence and context for the response.
                                                </Text>
                                            </Box>
                                        </VStack>
                                    </PopoverBody>
                                </PopoverContent>
                            </Popover>
                        );
                    });
                };

                // Shared helper: process text to replace citation patterns with clickable buttons.
                const processCitationText = (text) => {
                    const citationSplitRegex = /(\[.*?\.pdf,\s*pages?\s*[\d,\-–\s]+(?:,\s*pages?\s*\d+)*\]|\[WEB:\d+\])/g;
                    const parts = text.split(citationSplitRegex);
                    return parts.map((part, idx) => {
                        if (part.match(/\[.*?\.pdf,\s*pages?\s*/)) {
                            const citMatch = part.match(/\[(.*?\.pdf),\s*(pages?\s*[\d,\-–\s]+(?:,\s*pages?\s*\d+)*)\]/i);
                            const boundingRect = null;
                            // Extract surrounding text context for fuzzy text search on the PDF
                            const precedingText = idx > 0 ? parts[idx - 1] : '';
                            const citationText = precedingText.trim() || null;
                            return renderCitation(part, citationMap, boundingRect, citationText);
                        } else if (part.match(/\[WEB:\d+\]/)) {
                            return <React.Fragment key={`web-frag-${idx}`}>{renderWebCitation(part, message.sourcesList)}</React.Fragment>;
                        }
                        return part;
                    });
                };

                const citationComponents = {
                    ...markdownComponents,
                    p: ({children}) => {
                        const childrenArray = React.Children.toArray(children);
                        const content = childrenArray.map((child) => {
                            if (typeof child === "string") {
                                return processCitationText(child);
                            }
                            return child;
                        });
                        return <Text fontSize={15} color='black'>{content}</Text>
                    },
                    li: ({children}) => {
                        const childrenArray = React.Children.toArray(children);
                        const content = childrenArray.map((child) => {
                            if (typeof child === "string") {
                                return processCitationText(child);
                            }
                            return child;
                        });
                        return <ListItem fontSize={15} color='black'>{content}</ListItem>
                    },
                    ul: ({children}) => {
                        const childrenArray = React.Children.toArray(children);
                        const content = childrenArray.map((child) => {
                            if (typeof child === "string") {
                                return processCitationText(child);
                            }
                            return child;
                        });
                        return <UnorderedList my={'0.4rem'} fontSize={15}>{content}</UnorderedList>
                    },
                    ol: ({children}) => {
                        const childrenArray = React.Children.toArray(children);
                        const content = childrenArray.map((child) => {
                            if (typeof child === "string") {
                                return processCitationText(child);
                            }
                            return child;
                        });
                        return <OrderedList my={'0.4rem'} fontSize={15}>{content}</OrderedList>
                    },
                    blockquote: ({children}) => {
                        const childrenArray = React.Children.toArray(children);
                        const content = childrenArray.map((child) => {
                            if (typeof child === "string") {
                                return processCitationText(child);
                            }
                            return child;
                        });
                        return <Box as="blockquote" borderLeft="4px solid" fontSize={15}
                                    borderColor="gray.200" pl={"0.9rem"} my={"0.5rem"} fontStyle="italic">{content}</Box>
                    },
                    em: ({children}) => {
                        const childrenArray = React.Children.toArray(children);
                        const content = childrenArray.map((child) => {
                            if (typeof child === "string") {
                                return processCitationText(child);
                            }
                            return child;
                        });
                        return <Text as="em" fontStyle="italic" fontSize={15}>
                            {content}
                        </Text>
                    },
                };

                formattedMessage = escapeMarkdown(formattedMessage);
                
                return (
                    <>
                        <Markdown
                            remarkPlugins={[remarkGfm]}
                            components={citationComponents}
                            children={formattedMessage}
                        />
                        {message.sourcesList && message.sourcesList.length > 0 && (
                            <Box mt={6} pt={4}>
                                <Heading as="h3" size="sm" mb={3} color="gray.700" borderBottom="2px solid" borderColor="gray.200" pb={2}>
                                    Online Sources:
                                </Heading>
                                <UnorderedList spacing={2} fontSize="sm">
                                    {message.sourcesList.map((source) => (
                                        <ListItem key={source.id}>
                                            <Text as="span" fontWeight="bold" mr={2}>[{source.id}]</Text>
                                            <Link href={source.url} isExternal color="blue.500" textDecoration="underline" _hover={{ color: "blue.700" }}>
                                                {source.title}
                                            </Link>
                                        </ListItem>
                                    ))}
                                </UnorderedList>
                            </Box>
                        )}
                    </>
                );
            } else {
                return <Markdown
                    remarkPlugins={[remarkGfm]}
                    components={markdownComponents}
                    children={message.response} />
            }
        }
    }

    // const copyToClipboard = (text) => {
    //     navigator.clipboard.writeText(text)
    //         .then(() => {
    //             toast({
    //                 title: t("Copied!"),
    //                 status: "success",
    //                 duration: 3000,
    //                 isClosable: true,
    //             })
    //         })
    //         .catch(err => {
    //             console.error('Could not copy text: ', err);
    //         });
    // }
    const copyToClipboard = async (text, webSourcesList = []) => {
        // use the existing citationMap to replace PDF citations with numbers
        let processedText = text.replace(/\[(.*?\.pdf),\s*(pages?\s*[\d,\-–\s]+(?:,\s*pages?\s*\d+)*)\]/gi, (match, filename, pages) => {
            const citationKey = `${filename.trim()}_${pages.trim()}`;
            const citationNumber = citationMap.get(citationKey);
            return `[${citationNumber}]`;
        });

        // Build the "Web Sources" section (citations 1-N)
        const webSources = webSourcesList.length > 0 ? webSourcesList
            .map((source) => `[${source.id}] ${source.title}: ${source.url}`)
            .join("\n") : '';

        // Build the "PDF Citations" section (starting after web sources)
        const pdfCitations = Array.from(citationMap.entries())
            .map(([key, number]) => {
                const lastUnderscoreIndex = key.lastIndexOf('_');
                const beforeLastUnderscore = key.substring(0, lastUnderscoreIndex);
                const afterLastUnderscore = key.substring(lastUnderscoreIndex + 1);
                const pagesLabel = afterLastUnderscore.startsWith('page') ? afterLastUnderscore : `page ${afterLastUnderscore}`;
                return `[${number}] ${beforeLastUnderscore}, ${pagesLabel}`;
            })
            .join("\n");

        // Combine the main text and citations
        let result = processedText;
        if (webSources) {
            result += `\n\n**Web Sources**\n${webSources}`;
        }
        if (pdfCitations) {
            result += `\n\n**Uploaded File Citations**\n${pdfCitations}`;
        }
        const finalResult = result;

        try {
            // Use the enhanced copy function that supports formatted text
            const success = await copyFormattedToClipboard(finalResult);
            if (success) {
                toast({
                    title: t("Copied!"),
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
            } else {
                throw new Error('Copy failed');
            }
        } catch (err) {
            console.error('Could not copy text: ', err);
            toast({
                title: t("Copy failed"),
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };



    if (message.user === "Gemini" && message.response === "") {
        return (
            <Box
                key={message.id}
                bg={message.user.toLowerCase() === 'user' ? userBgColor : claudeBgColor}
                color={message.user.toLowerCase() === 'gemini' ? claudeColor : 'white'}
                alignSelf={message.user.toLowerCase() === 'user' ? 'flex-end' : 'flex-start'}
                p='1.5rem'
                ref={isLastMessage ? lastMessageRef : null}
                width="100%"
                justifyContent={"flex-start"}
                alignItems={"center"}
                flexDirection={"row"}
                display={"flex"}
            >
                <Box
                    width="2rem"
                    height="2rem"
                    borderRadius="full"
                    overflow="hidden"
                    boxShadow="0 4px 6px rgba(0, 0, 0, 0.1)"
                    mr="1rem"
                >
                    <Image
                        src='/Justify_j_always_white_background.jpg'
                        alt="Company Logo"
                        width="100%"
                        height="100%"
                        transform="scale(1.1)"
                        objectFit="cover"
                    />
                </Box>
                <Box flex={1} mt={2}>
                    {isLargeContextProcessing ? (
                        <LargeContextThinkingDisplay
                            isProcessing={true}
                            onCitationClick={onCitationClick}
                            historyKey={message.id}
                            celeryTaskId={largeContextTaskId}
                            progressHistory={largeContextProgressHistory}
                        />
                    ) : (
                        <ThinkingDisplay
                            isThinking={isThinking}
                            live={true}
                            onCitationClick={onCitationClick}
                            socketGeneration={socketGeneration}
                            historyKey={message.id}
                        />
                    )}
                </Box>
            </Box>
        )
    }

    else {
        return (
            <Box
                key={message.id}
                bg={message.user.toLowerCase() === 'user' ? "white" : claudeBgColor}
                color={message.user.toLowerCase() === 'gemini' ? claudeColor : 'black'}
                p='1.5rem'
                ref={isLastMessage ? lastMessageRef : null}
                width="100%"
                alignItems={"flex-start"}
                flexDirection={"row"}
                display={"flex"}
            >
                <Box
                    width="2rem"  // Adjust the width as needed
                    height="2rem"  // Adjust the height as needed
                    borderRadius="full"  // Makes the image circular
                    overflow="hidden"
                    boxShadow="0 4px 6px rgba(0, 0, 0, 0.1)"
                    mr="1rem"
                >
                    {auth.isLoggedIn && message.user === "User" ?
                        <Avatar name={firstName + " " + lastName} width={"100%"} height={"100%"} size={"sm"}/> :
                        !auth.isLoggedIn && message.user === "User" ?
                            <Image
                                src={"https://static.vecteezy.com/system/resources/thumbnails/005/544/718/small/profile-icon-design-free-vector.jpg"}
                                alt="Company Logo"
                                width="100%"
                                height="100%"
                                objectFit="cover"
                            /> :
                            <Image
                                src='/Justify_j_always_white_background.jpg'
                                alt="Company Logo"
                                width="100%"
                                height="100%"
                                transform="scale(1.1)"
                                objectFit="cover"
                            />}



                </Box>
                <Box display={'flex'} flexDirection={"column"} flex="1">
                    {message.user.toLowerCase() === 'gemini' && !isLargeContextProcessing ?
                        <Box flex={1} mb={7} mt={2}>
                            <ThinkingDisplay
                                isThinking={isThinking}
                                live={false}
                                onCitationClick={onCitationClick}
                                socketGeneration={socketGeneration}
                                isCollapsed={true}
                                keepAfterDone={true}
                                historyKey={message.id}
                            />
                        </Box> : null}
                    {message.user.toLowerCase() === 'user' && message.type !== "general_multidoc" && (
                        <Text fontWeight={"700"} color={'black'} marginBottom={'0.8rem'}>
                            {whichMessageHeader(message.type, message)}</Text>
                    )}
                    {message.type === "ask_image" && message.user === "User" ?
                        <Image src={message.selection_text} alt="Image asked about" width="100%" height="100%"
                               objectFit="cover"/>
                        :
                        whichMessage(message)
                    }


                    <Box display={'flex'} flexDirection={"row"} alignItems={'center'}>
                        {/* {(message.user === "Gemini" && !auth.isThinking && !isLastMessage) ? <FollowUpButton
                            sessionToken={sessionToken} socketID={socketID}
                            claudeMessage={message}/> : null} */}
                        {message.user === "Gemini" && !auth.isThinking ?
                            <Tooltip hasArrow label={t("Copy this response")} bg='black' color="white" placement={'right'}>
                                <span>
                                    <Icon
                                        as={FiCopy}
                                        boxSize="1.4rem" // or any other size you desire
                                        cursor="pointer"
                                        mt={'1rem'}
                                        ml={'0.1rem'}
                                        onClick={() => copyToClipboard(message.response, message.sourcesList || [])}
                                    />
                                </span>
                            </Tooltip>
                            : null}
                    </Box>

                </Box>
            </Box>
        )
    }
}, (prevProps, nextProps) => {
    // Only re-render if these props change
    return prevProps.message.id === nextProps.message.id &&
        prevProps.isLastMessage === nextProps.isLastMessage &&
        prevProps.isThinking === nextProps.isThinking &&
        prevProps.currentThinkingMessage === nextProps.currentThinkingMessage &&
        prevProps.message.response === nextProps.message.response &&
        prevProps.message.sourcesList === nextProps.message.sourcesList &&
        prevProps.message.supportsList === nextProps.message.supportsList &&
        prevProps.isLargeContextProcessing === nextProps.isLargeContextProcessing &&
        prevProps.largeContextTaskId === nextProps.largeContextTaskId &&
        prevProps.largeContextProgressHistory?.length === nextProps.largeContextProgressHistory?.length;
});





